import React, { useId, useMemo, useState } from "react";

interface SparklineChartProps {
  /** 12-month series, ascending by month (oldest → newest). `null` = gap. */
  values: (number | null)[];
  /** Formats a value for the accessible label and hover tooltip. */
  formatFn: (v: number) => string;
  /**
   * "Lower is better" metric (churn / CAC / payback): a downward trend is good
   * (green), upward is bad (red). Default false (higher is better).
   */
  invertDelta?: boolean;
  /** Accessible name of the metric this sparkline belongs to. */
  metricLabel: string;
  width?: number;
  height?: number;
}

const W = 96;
const H = 24;
const PAD = 2;

interface Point {
  x: number;
  y: number;
  value: number;
  index: number;
}

/**
 * Mini SVG sparkline with an individually auto-scaled Y axis (shows direction,
 * not absolute values). Renders a `<title>` plus an `aria-label` describing the
 * 12-month trend from first to last value, so colour is never the only signal
 * (WCAG 1.4.1). Hovering a point reveals its formatted value.
 */
const SparklineChart: React.FC<SparklineChartProps> = ({
  values,
  formatFn,
  invertDelta = false,
  metricLabel,
  width = W,
  height = H,
}) => {
  const titleId = useId();
  const [hover, setHover] = useState<Point | null>(null);

  const { points, firstVal, lastVal } = useMemo(() => {
    const present = values
      .map((v, index) => ({ v, index }))
      .filter((p): p is { v: number; index: number } => p.v !== null);

    if (present.length === 0) {
      return { points: [] as Point[], firstVal: null, lastVal: null };
    }

    const nums = present.map((p) => p.v);
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const span = max - min || 1; // avoid /0 when the series is flat
    const innerW = width - PAD * 2;
    const innerH = height - PAD * 2;
    // X is spaced over the full 12-slot axis so gaps are visually preserved.
    const lastSlot = values.length - 1 || 1;

    const pts: Point[] = present.map((p) => ({
      x: PAD + (p.index / lastSlot) * innerW,
      // Invert Y because SVG's origin is top-left; higher value = higher on screen.
      y: PAD + innerH - ((p.v - min) / span) * innerH,
      value: p.v,
      index: p.index,
    }));

    return {
      points: pts,
      firstVal: present[0].v,
      lastVal: present[present.length - 1].v,
    };
  }, [values, width, height]);

  const direction = useMemo<"rosnący" | "malejący" | "płaski">(() => {
    if (firstVal === null || lastVal === null) return "płaski";
    if (lastVal > firstVal) return "rosnący";
    if (lastVal < firstVal) return "malejący";
    return "płaski";
  }, [firstVal, lastVal]);

  // Colour reflects good/bad, not raw direction (matches MetricCard delta logic).
  const trendUp = direction === "rosnący";
  const trendDown = direction === "malejący";
  const isGood = invertDelta ? trendDown : trendUp;
  const isBad = invertDelta ? trendUp : trendDown;
  const stroke = isGood
    ? "rgb(22 163 74)" // green-600
    : isBad
      ? "rgb(220 38 38)" // red-600
      : "rgb(115 115 115)"; // neutral-500

  const ariaLabel =
    points.length === 0
      ? `${metricLabel}: trend 12 mies. — brak danych`
      : `${metricLabel}: trend 12 mies., od ${formatFn(
          firstVal as number,
        )} do ${formatFn(lastVal as number)}, kierunek ${direction}`;

  if (points.length === 0) {
    return (
      <svg
        role="img"
        aria-label={ariaLabel}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
      >
        <title id={titleId}>{ariaLabel}</title>
        <line
          x1={PAD}
          y1={height / 2}
          x2={width - PAD}
          y2={height / 2}
          stroke="rgb(212 212 212)"
          strokeDasharray="2 2"
          strokeWidth={1}
        />
      </svg>
    );
  }

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");
  const last = points[points.length - 1];

  return (
    <span className="relative inline-flex">
      <svg
        role="img"
        aria-label={ariaLabel}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
      >
        <title id={titleId}>{ariaLabel}</title>
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Emphasise the latest point. */}
        <circle cx={last.x} cy={last.y} r={1.8} fill={stroke} />
        {/* Invisible wide hit-targets give each point a hoverable area. */}
        {points.map((p) => (
          <circle
            key={p.index}
            cx={p.x}
            cy={p.y}
            r={5}
            fill="transparent"
            onMouseEnter={() => setHover(p)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {hover && (
          <circle
            cx={hover.x}
            cy={hover.y}
            r={2.4}
            fill={stroke}
            stroke="white"
            strokeWidth={0.8}
          />
        )}
      </svg>
      {hover && (
        <span
          role="tooltip"
          className="pointer-events-none absolute -top-7 left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-medium text-white shadow"
        >
          {formatFn(hover.value)}
        </span>
      )}
    </span>
  );
};

export default SparklineChart;
